import { useCallback, useState, useId } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DragDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  accept?: string;
  maxFiles?: number;
  maxSize?: number; // in MB
  className?: string;
  resetKey?: number; // Force reset of input element
}

export function DragDropZone({ 
  onFilesSelected, 
  accept = '*', 
  maxFiles = 10,
  maxSize = 10,
  className,
  resetKey = 0
}: DragDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const uniqueId = useId();
  const inputId = `file-upload-${uniqueId}`;

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files
      .filter(file => file.size <= maxSize * 1024 * 1024)
      .slice(0, maxFiles);

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
    
    // Reset file input on drop
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) input.value = '';
  }, [onFilesSelected, maxFiles, maxSize, inputId]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).slice(0, maxFiles);
      onFilesSelected(files);
      // Reset input so same files can be selected again
      e.target.value = '';
    }
  };

  return (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
        isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
        className
      )}
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        type="file"
        id={inputId}
        key={resetKey}
        className="hidden"
        multiple
        accept={accept}
        onChange={handleFileInput}
      />
      <label
        htmlFor={inputId}
        className="cursor-pointer flex flex-col items-center gap-2"
      >
        <Upload className={cn(
          "w-10 h-10 transition-colors",
          isDragging ? "text-primary" : "text-muted-foreground"
        )} />
        <p className="text-sm font-medium">
          {isDragging ? "Drop bestanden hier" : "Sleep bestanden hierheen of klik om te selecteren"}
        </p>
        <p className="text-xs text-muted-foreground">
          Max {maxFiles} bestanden • {maxSize}MB per bestand
        </p>
      </label>
    </div>
  );
}